import { useEffect, useState } from "react";
import { apiConstants, post } from "../../../common/constants";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import { exportToCSV, formatDate } from "../../../helpers/UtilityHelper";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import _ from 'lodash';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        wordBreak: 'break-word',
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

function MerchantCODConfig() {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [paginatedData, setPaginatedData] = useState([]);

    useEffect(() => {
        post(apiConstants.MERCHANT_SEARCH_CACHED, {
            skipCount: true,
            limit: 500,
            andFilters: [{ field: "kyc_status", operator: "eq", value: "KYC_APPROVED" }],
            includes: "name,status,merchantId,id,address,city,state,pincode,country,codRemittanceConfig,createdDate"
        }).then((res) => {
            let d = res?.data?.data;
            d = _.filter(d, (o) => o?.status === "KYC_APPROVED");
            setData(d);
        });
    }, []);

    useEffect(() => {
        const start = page * sizePerPage;
        setPaginatedData(data.slice(start, start + sizePerPage));
    }, [data, page, sizePerPage]);

    const columns = [
        {
            field: "merchantId",
            headerName: "Merchant ID",
            headerAlign: "left", align: "left",
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => (
                <CellLine muted>{row?.merchantId || '—'}</CellLine>
            ),
        },
        {
            field: "name",
            headerName: "Merchant Name",
            headerAlign: "left", align: "left",
            hideSortIcons: true, minWidth: 200, flex: 1.5,
            renderCell: ({ row }) => (
                <CellLine bold>{row?.name || '—'}</CellLine>
            ),
        },
        {
            field: "status",
            headerName: "KYC Status",
            headerAlign: "left", align: "left",
            hideSortIcons: true, minWidth: 130, flex: 0.8,
            renderCell: ({ row }) => (
                <Box component="span" sx={{
                    display: 'inline-block', px: '8px', py: '3px', borderRadius: '5px',
                    fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                    bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                }}>
                    {row?.status || '—'}
                </Box>
            ),
        },
        {
            field: "codRemittanceConfig",
            headerName: "COD Cycle (days)",
            headerAlign: "center", align: "center",
            hideSortIcons: true, minWidth: 140, flex: 0.8,
            valueGetter: (params) => params.row?.codRemittanceConfig?.cycleOfDays,
            renderCell: ({ row }) => {
                const val = row?.codRemittanceConfig?.cycleOfDays;
                return val != null ? (
                    <Box sx={{
                        display: 'inline-block', px: '10px', py: '3px', borderRadius: '5px',
                        fontSize: '12px', fontWeight: 700,
                        bgcolor: '#eff6ff', color: '#1565c0', border: '1px solid #bfdbfe',
                    }}>
                        {val}d
                    </Box>
                ) : <CellLine muted>—</CellLine>;
            },
        },
        {
            field: "postDeliverySettlementDays",
            headerName: "Post Delivery Days",
            headerAlign: "center", align: "center",
            hideSortIcons: true, minWidth: 150, flex: 0.8,
            valueGetter: (params) => params.row?.codRemittanceConfig?.postDeliverySettlementDays,
            renderCell: ({ row }) => {
                const val = row?.codRemittanceConfig?.postDeliverySettlementDays;
                return val != null ? (
                    <Box sx={{
                        display: 'inline-block', px: '10px', py: '3px', borderRadius: '5px',
                        fontSize: '12px', fontWeight: 700,
                        bgcolor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb',
                    }}>
                        {val}d
                    </Box>
                ) : <CellLine muted>—</CellLine>;
            },
        },
        {
            field: "earlyCODEnabled",
            headerName: "Early COD",
            headerAlign: "center", align: "center",
            hideSortIcons: true, width: 110,
            valueGetter: (params) => params.row?.codRemittanceConfig?.earlyCODEnabled,
            renderCell: ({ row }) => {
                const val = row?.codRemittanceConfig?.earlyCODEnabled;
                if (val == null) return <CellLine muted>—</CellLine>;
                return (
                    <Box component="span" sx={{
                        display: 'inline-block', px: '8px', py: '3px', borderRadius: '5px',
                        fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                        bgcolor: val ? '#f0fdf4' : '#fef2f2',
                        color: val ? '#16a34a' : '#dc2626',
                        border: `1px solid ${val ? '#bbf7d0' : '#fecaca'}`,
                    }}>
                        {val ? 'Yes' : 'No'}
                    </Box>
                );
            },
        },
        {
            field: "codRemittanceConfig1",
            headerName: "Last Updated",
            headerAlign: "left", align: "left",
            hideSortIcons: true, minWidth: 150, flex: 1,
            valueGetter: (params) => params.row?.codRemittanceConfig?.updateTime,
            renderCell: ({ row }) => {
                const t = row?.codRemittanceConfig?.updateTime;
                return (
                    <CellStack>
                        <CellLine muted>{t ? formatDate(t) : '—'}</CellLine>
                    </CellStack>
                );
            },
        },
    ];

    return (
        <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Box sx={{
                px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
            }}>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Merchant Credit Limit</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>
                        KYC-approved merchants with their COD remittance configuration
                    </Typography>
                </Box>
                {data.length > 0 && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={() => exportToCSV(columns, data, 'MerchantCreditLimit')}
                        sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderColor: '#e0e0e0', color: '#374151', '&:hover': { borderColor: '#1565c0', color: '#1565c0' } }}
                    >
                        Export CSV
                    </Button>
                )}
            </Box>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <ProzoDataGrid
                    columns={columns}
                    rows={paginatedData}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    rowCount={data.length}
                    getRowHeight={() => 'auto'}
                    getRowId={(row) => row.id}
                />
            </CardContent>
        </Card>
    );
}

export default MerchantCODConfig;
